import React, { useState } from "react";
import { Box } from "@chakra-ui/react";
import { FiChevronRight } from "react-icons/fi";

const SUBMENU_WIDTH = 220;

export interface ColumnContextSubMenuProps {
	/** Label shown in the parent menu row (with a trailing chevron). */
	label: React.ReactNode;
	/** data-testid for the parent row. Submenu container is `${testId}-submenu`. */
	testId?: string;
	/**
	 * When true, render the flyout on the LEFT side of the parent menu instead
	 * of the right. The parent menu computes this from viewport math.
	 */
	flipLeft?: boolean;
	/** Submenu rows (MenuItem instances). */
	children: React.ReactNode;
}

const labelStyle: React.CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	fontSize: 14,
	padding: "8px 12px",
	cursor: "pointer",
	color: "#1A202C",
};

/**
 * Reusable column-header submenu row. Renders a clickable parent row with a
 * trailing chevron; on hover, displays a flyout panel containing the supplied
 * `children` (which must be `MenuItem` instances or compatible rows).
 *
 * Self-manages hover open state. Visual styles mirror the parent context-menu
 * styling so submenus look consistent regardless of caller.
 */
export const ColumnContextSubMenu: React.FC<ColumnContextSubMenuProps> = ({
	label,
	testId,
	flipLeft,
	children,
}) => {
	const [open, setOpen] = useState(false);

	const submenuStyle: React.CSSProperties = flipLeft
		? { right: "100%", top: 0 }
		: { left: "100%", top: 0 };

	return (
		<Box
			role="menuitem"
			data-testid={testId}
			onMouseEnter={() => setOpen(true)}
			onMouseLeave={() => setOpen(false)}
			position="relative"
		>
			<div style={labelStyle}>
				<span>{label}</span>
				<FiChevronRight size={14} />
			</div>
			{open && (
				<Box
					data-testid={testId ? `${testId}-submenu` : undefined}
					position="absolute"
					style={submenuStyle}
					bg="white"
					boxShadow="lg"
					borderRadius="md"
					border="1px solid"
					borderColor="gray.200"
					minW={`${SUBMENU_WIDTH}px`}
					py={1}
				>
					{children}
				</Box>
			)}
		</Box>
	);
};

export default ColumnContextSubMenu;
